import { Card, CardContent } from "@rumbledirect/ui";
import { Suspense, useState, useRef, useCallback, useEffect } from "react";
import { ButtonGroup } from "@rumble-it/common-components/components";
import { Loading } from "@/components/common/loading";
import { RunConsoleModal } from "./components/RunConsoleModal";
import { BenchmarkRunsTable } from "./components/BenchmarkRunsTable";
import { GenerateSyntheticEmailsModal } from "./components/GenerateSyntheticEmailsModal";
import { FolderContentsViewerModal } from "./components/FolderContentsViewerModal";
import { Button } from "@/components/common/button";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  fetchAgents,
  fetchAgentModelCatalog,
  fetchRuleActions,
  fetchBenchmarkInputFolders,
  uploadBenchmarkInputFolder,
  deleteBenchmarkInputFolder,
  renameBenchmarkInputFolder,
  createBenchmarkConfig,
  updateBenchmarkConfig,
} from "@/lib/api/resources/benchmark";
import { fetchAiProfiles } from "@/lib/api/resources/agents";
import {
  Modal,
  Spin,
  Steps,
  Select,
  Popconfirm,
  Tooltip,
  Tabs,
  Input,
  Button as AntButton,
  Slider,
  InputNumber,
  Switch,
  Pagination,
  message,
} from "antd";
import {
  FolderOutlined,
  UploadOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { AgentTile } from "./components/AgentTile";
import { CapabilityTile } from "./components/CapabilityTile";
import { BenchmarkConfigsTable } from "./components/BenchmarkConfigsTable";
import {
  BenchmarkParametersStep,
  EMPTY_PARAMETERS,
  type ParameterValues,
} from "./components/BenchmarkParametersStep";
import {
  BenchmarkJudgingStep,
  DEFAULT_JUDGING,
  type JudgingValues,
} from "./components/BenchmarkJudgingStep";

const STEP_TITLES = [
  "Agent",
  "Target",
  "Capability",
  "Parameters",
  "Judging",
  "Name",
];
const LAST_STEP = STEP_TITLES.length - 1;

const CLASSIFY_BY_AGENT_TYPES = [
  "image_capture_classify_car_angle_by_agent",
  "image_capture_classify_car_by_agent",
  "image_capture_classify_image_quality_by_agent",
];

// Friendly labels for the classify rule-action types (the raw enum value is unfriendly to show).
const CLASSIFY_TYPE_LABELS: Record<string, string> = {
  image_capture_classify_car_angle_by_agent: "Car angle classification",
  image_capture_classify_car_by_agent: "Car classification",
  image_capture_classify_image_quality_by_agent: "Image quality classification",
};

const MAIL_DIGEST = "mail_digest";
const CLASSIFY_BY_AGENT = "classify_by_agent";
const IMAGE_EXTRACT = "image_extract";

// The input sources (capabilities) each benchmark type can be fed with (config.input.type). Add an
// entry when a new BenchmarkInputSource is implemented on the backend.
const INPUT_SOURCES: Record<string, { value: string; label: string }[]> = {
  [CLASSIFY_BY_AGENT]: [
    { value: "image_folder", label: "Image folder" },
    { value: "media", label: "Media records (by id)" },
  ],
  [MAIL_DIGEST]: [{ value: "eml_folder", label: "Email folder (.eml)" }],
  [IMAGE_EXTRACT]: [
    { value: "image_folder", label: "Image folder" },
    { value: "media", label: "Media records (by id)" },
  ],
};

const FOLDER_SOURCES = ["image_folder", "eml_folder"];

const collection = (data: any): any[] =>
  Array.isArray(data) ? data : data?.member || data?.["hydra:member"] || [];

// Normalise an agent reference (an IRI like ".../agents/<uuid>" or a bare UUID) to its bare UUID, so we
// can match a rule action's data.agentId exactly instead of with fragile substring checks.
const agentUuid = (value: any): string => {
  if (!value) return "";
  // "@id"/"id" may themselves be a full IRI, so route them through the same
  // split-and-trim as the plain-string case instead of returning them as-is.
  const raw = typeof value === "string" ? value : (value["@id"] ?? value.id);
  if (typeof raw !== "string") return "";
  return (raw.split("/").pop() || "").trim().toLowerCase();
};

// Media ids are entered free-form (comma / whitespace / newline separated); normalise to a clean list.
const parseMediaIds = (raw: string): string[] =>
  raw
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter(Boolean);

const buildParameters = (p: ParameterValues): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  if (p.override) out.override = p.override;
  if (p.aiProfileId) out.aiProfileId = p.aiProfileId;
  if (p.instructions.trim()) out.instructions = p.instructions.trim();
  if (p.maxTokensPerSession !== null)
    out.maxTokensPerSession = p.maxTokensPerSession;
  return out;
};

export default function BenchmarkCockpitPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "configs";

  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [previewFolder, setPreviewFolder] = useState<string | null>(null);
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [configName, setConfigName] = useState("");
  const [agentListPage, setAgentListPage] = useState(1);
  const [folderListPage, setFolderListPage] = useState(1);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  // The benchmark target: either a classify rule action, or a standalone capability (mail digest) when
  // the agent has no classify rule action. These are mutually exclusive.
  const [selectedRuleAction, setSelectedRuleAction] = useState<string | null>(
    null,
  );
  const [standaloneCapability, setStandaloneCapability] = useState<
    string | null
  >(null);
  const [inputType, setInputType] = useState<string>("image_folder");
  const [folder, setFolder] = useState("");
  const [uploadFolderName, setUploadFolderName] = useState("");
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [mediaIds, setMediaIds] = useState("");
  const [parameters, setParameters] =
    useState<ParameterValues>(EMPTY_PARAMETERS);
  const [judging, setJudging] = useState<JudgingValues>(DEFAULT_JUDGING);

  const { data: agentsData, isLoading: agentsLoading } = useQuery({
    queryKey: ["agents"],
    queryFn: fetchAgents,
  });
  const { data: ruleActionsData, isLoading: ruleActionsLoading } = useQuery({
    queryKey: ["rule_actions"],
    queryFn: fetchRuleActions,
  });
  const { data: aiProfilesData } = useQuery({
    queryKey: ["ai_profiles"],
    queryFn: fetchAiProfiles,
  });
  const { data: foldersData } = useQuery({
    queryKey: ["benchmark_input_folders"],
    queryFn: fetchBenchmarkInputFolders,
  });
  const { data: modelCatalogData } = useQuery({
    queryKey: ["agent_model_catalog"],
    queryFn: fetchAgentModelCatalog,
    // The catalog is static bridge metadata; don't refetch it on every remount/refocus.
    staleTime: 1000 * 60 * 60,
  });

  const closeAndReset = () => {
    setIsModalOpen(false);
    setEditingConfigId(null);
    setStep(0);
    setConfigName("");
    setAgentListPage(1);
    setSelectedAgent(null);
    setSelectedRuleAction(null);
    setStandaloneCapability(null);
    setInputType("image_folder");
    setFolder("");
    setMediaIds("");
    setParameters(EMPTY_PARAMETERS);
    setJudging(DEFAULT_JUDGING);
    setEditingConfigId(null);
    setInitialPagesSet(false);
  };

  const [initialPagesSet, setInitialPagesSet] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);

  useEffect(() => {
    if (isModalOpen && editingConfigId && !initialPagesSet) {
      const agents = collection(agentsData);
      const folders = collection(foldersData);

      if (agents.length > 0 && folders.length > 0) {
        // Init agent page
        const targetAgentUuid = agentUuid(selectedAgent);
        const agentIndex = agents.findIndex(
          (a) => agentUuid(a.id || a["@id"]) === targetAgentUuid,
        );
        if (agentIndex >= 0) setAgentListPage(Math.floor(agentIndex / 4) + 1);

        // Init folder page
        let foundFolderOpts: any[] = [];
        for (const item of folders) {
          if (item.type === inputType) foundFolderOpts = item.folders ?? [];
        }
        const folderIndex = foundFolderOpts.findIndex(
          (f) => (f.id || f) === folder,
        );
        if (folderIndex >= 0)
          setFolderListPage(Math.floor(folderIndex / 6) + 1);

        setInitialPagesSet(true);
      }
    }
  }, [
    isModalOpen,
    editingConfigId,
    initialPagesSet,
    agentsData,
    foldersData,
    selectedAgent,
    folder,
    inputType,
  ]);

  const mutation = useMutation({
    mutationFn: (params: {
      id?: string;
      name: string;
      agentId: string;
      ruleActionId?: string;
      config: Record<string, unknown>;
    }) => {
      const payload = {
        name: params.name,
        agent: agentUuid(params.agentId),
        ruleAction: params.ruleActionId,
        config: params.config,
      };
      if (params.id) {
        return updateBenchmarkConfig(params.id, payload);
      }
      return createBenchmarkConfig(payload);
    },
    onSuccess: () => {
      message.success("Benchmark configuration saved successfully!");
      queryClient.invalidateQueries({ queryKey: ["benchmarkConfigs"] });
      closeAndReset();
    },
    onError: () => {
      message.error("Failed to save benchmark configuration.");
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({
      type,
      folderName,
      files,
      onProgress,
    }: {
      type: string;
      folderName: string;
      files: FileList | File[];
      onProgress?: (progress: number) => void;
    }) => uploadBenchmarkInputFolder(type, folderName, files, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["benchmark_input_folders"] });
      // The server stores the folder under a sanitized name (any char outside [A-Za-z0-9_-]
      // becomes "-"); mirror that here so the selected `folder` id matches the stored folder
      // instead of the raw, unsanitized name.
      const storedName = uploadFolderName
        .trim()
        .replace(/[^a-zA-Z0-9_-]/g, "-");
      setFolder(storedName);
      setUploadFolderName("");
      message.success("Folder uploaded successfully.");
    },

    onError: (error: any) => {
      // Uploads are chunked into several sequential requests; a failure partway through can still
      // have persisted earlier chunks server-side, so refresh the list instead of leaving it stale.
      queryClient.invalidateQueries({ queryKey: ["benchmark_input_folders"] });
      message.error(
        error?.response?.data?.detail ||
          error?.message ||
          "Failed to upload folder.",
      );
    },

    onSettled: () => {
      setUploadProgress(null);
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: ({ type, folderName }: { type: string; folderName: string }) =>
      deleteBenchmarkInputFolder(type, folderName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["benchmark_input_folders"] });
      message.success("Folder deleted successfully.");
      if (folder) setFolder("");
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail || "Failed to delete folder.",
      );
    },
  });

  const renameFolderMutation = useMutation({
    mutationFn: ({
      type,
      folderName,
      newName,
    }: {
      type: string;
      folderName: string;
      newName: string;
    }) => renameBenchmarkInputFolder(type, folderName, newName),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["benchmark_input_folders"] });
      message.success("Folder renamed successfully.");
      if (folder === variables.folderName) {
        setFolder(variables.newName);
      }
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail || "Failed to rename folder.",
      );
    },
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    // Chunked uploads span several sequential requests; without this guard a second selection
    // mid-upload would start a concurrent mutate() call racing the first over shared mutation state.
    if (uploadMutation.isPending) return;

    const firstPath = e.target.files[0].webkitRelativePath || "";
    let defaultName = "new-folder";
    if (firstPath.includes("/")) {
      defaultName = firstPath.split("/")[0];
    }

    const name = window.prompt("Name this folder on the server:", defaultName);
    if (name && name.trim()) {
      setUploadFolderName(name.trim());
      // FileList is a live collection. If we clear the input below, the FileList becomes empty
      // before the async uploadMutation processes it. We MUST copy it to an array!
      const filesArray = Array.from(e.target.files);
      uploadMutation.mutate({
        type: inputType,
        folderName: name.trim(),
        files: filesArray,
        onProgress: (progress: number) => {
          setUploadProgress(progress);
        },
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const agentsArray = collection(agentsData);
  const ruleActionsArray = collection(ruleActionsData);
  const aiProfilesArray = collection(aiProfilesData);

  // The API serializes `ruleActionType` as a translated label object (no value), so prefer the explicit
  // `ruleActionTypeValue` string field; fall back to the older string / {value} shapes for safety.
  const ruleActionTypeValue = (ra: any): string =>
    typeof ra?.ruleActionTypeValue === "string"
      ? ra.ruleActionTypeValue
      : typeof ra?.ruleActionType === "string"
        ? ra.ruleActionType
        : (ra?.ruleActionType?.value ?? "");

  const selectedAgentUuid = agentUuid(selectedAgent);
  // The selected agent's read-safe connection (provider/model/options) primes and frames the
  // Parameters step; the model catalog feeds its per-provider model picker.
  const selectedAgentObj = agentsArray.find(
    (a: any) => agentUuid(a.id || a["@id"]) === selectedAgentUuid,
  );
  const selectedAgentConnection = selectedAgentObj?.aiConnection ?? null;

  const selectedProfileId = selectedAgentObj?.aiProfileId ?? undefined;
  const selectedAgentProfile = selectedProfileId
    ? aiProfilesArray.find(
        (p) =>
          p.id === selectedProfileId || p["@id"]?.endsWith(selectedProfileId),
      )
    : null;

  const modelCatalog = modelCatalogData ?? [];
  const classifyCapabilities: Array<{
    type: string;
    label: string;
    description: string;
    ruleActionId: string;
  }> = [];

  for (const ra of ruleActionsArray) {
    const type = ruleActionTypeValue(ra);
    if (!CLASSIFY_BY_AGENT_TYPES.includes(type)) continue;
    if (!selectedAgentUuid || agentUuid(ra.data?.agentId) !== selectedAgentUuid)
      continue;
    classifyCapabilities.push({
      type,
      label: ra.name || (CLASSIFY_TYPE_LABELS[type] ?? type),
      description:
        ra.description ||
        "Run this agent's classification over a set of images and score the result.",
      ruleActionId: ra.id || ra["@id"],
    });
  }
  // Neither classify nor a rule action: offer a standalone capability instead. Which one depends on the
  // agent — a vision agent gets image extraction, everything else the email digest.
  const showStandaloneFallback =
    !ruleActionsLoading && classifyCapabilities.length === 0;

  // The resolved run type, derived from the chosen target.
  const benchmarkType = selectedRuleAction
    ? CLASSIFY_BY_AGENT
    : standaloneCapability;
  const inputSources = benchmarkType
    ? (INPUT_SOURCES[benchmarkType] ?? [])
    : [];

  // Folders available on the server for the active folder-based capability.
  const foldersByType: Record<string, string[]> = {};
  for (const entry of collection(foldersData)) {
    if (entry?.type) foldersByType[entry.type] = entry.folders ?? [];
  }
  const folderOptions = foldersByType[inputType] ?? [];

  const parsedMediaIds = parseMediaIds(mediaIds);
  const testDataMissing =
    (FOLDER_SOURCES.includes(inputType) && folder.trim() === "") ||
    (inputType === "media" && parsedMediaIds.length === 0);

  // Picking an agent pre-fills the parameters with that agent's defaults (editable per run) and clears
  // the now-stale target selection.
  const selectAgent = (agent: any) => {
    setSelectedAgent(agent.id || agent["@id"]);
    setSelectedRuleAction(null);
    setStandaloneCapability(null);
    // Prime the per-run parameters from the agent's connection (model + inference options) and its own
    // fields, so the form starts equal to the agent and the user only changes what they want to test.
    // aiConnection.options is camelCase on read; the run override is snake_case on the wire, so map
    // the well-known keys across.
    const connectionOptions =
      agent.aiConnection?.options && !Array.isArray(agent.aiConnection.options)
        ? agent.aiConnection.options
        : {};
    const opts: Record<string, unknown> = {};
    if (connectionOptions.temperature != null)
      opts.temperature = connectionOptions.temperature;
    if (connectionOptions.maxTokens != null)
      opts.max_tokens = connectionOptions.maxTokens;
    if (connectionOptions.topP != null) opts.top_p = connectionOptions.topP;

    const profileId = agent.aiProfileId ?? undefined;
    const profile = profileId
      ? aiProfilesArray.find(
          (p) => p.id === profileId || p["@id"]?.endsWith(profileId),
        )
      : null;

    setParameters({
      ...EMPTY_PARAMETERS,
      instructions: agent.instructions || "",
      // Generation options (temperature, top_p, ...) live in override.options (snake_case), primed
      // from the agent's own connection so the form starts equal to the agent; the user tweaks what
      // they test.
      override: Object.keys(opts).length > 0 ? { options: { ...opts } } : null,
      maxTokensPerSession:
        agent.maxTokensPerSession ?? profile?.contextWindowOutput ?? null,
    });
  };

  const selectRuleAction = (id: string) => {
    setSelectedRuleAction(id);
    setStandaloneCapability(null);
    setInputType("image_folder");
    setFolder("");
    setMediaIds("");
  };

  const selectMailDigest = () => {
    setStandaloneCapability(MAIL_DIGEST);
    setSelectedRuleAction(null);
    setInputType("eml_folder");
    setFolder("");
    setMediaIds("");
    setJudging((prev) => ({ ...prev, mode: "ground_truth" }));
  };

  // Standalone image-extraction capability for vision agents without a classify rule action: feed a
  // folder of images to the agent and score the structured data it extracts (judged in image mode).
  const selectImageExtraction = () => {
    setStandaloneCapability(IMAGE_EXTRACT);
    setSelectedRuleAction(null);
    setInputType("image_folder");
    setFolder("");
    setMediaIds("");
    setJudging((prev) => ({ ...prev, mode: "image" }));
  };

  const selectInputType = (value: string) => {
    setInputType(value);
    setFolder("");
    setMediaIds("");
  };

  const canProceed =
    step === 0
      ? !!selectedAgent
      : step === 1
        ? !!benchmarkType
        : step === 2
          ? !testDataMissing
          : step === 5
            ? !!configName.trim()
            : true;
  const canStart =
    !!configName.trim() &&
    !!selectedAgent &&
    !!benchmarkType &&
    !testDataMissing;

  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (step !== 0) return; // Only apply scroll pagination on the Agent list step
      if (scrollTimeout.current) return;

      const delta = e.deltaY;
      const totalPages = Math.ceil(agentsArray.length / 4);

      if (delta > 20) {
        if (agentListPage < totalPages) {
          setAgentListPage((p) => p + 1);
          scrollTimeout.current = setTimeout(() => {
            scrollTimeout.current = null;
          }, 400);
        }
      } else if (delta < -20) {
        if (agentListPage > 1) {
          setAgentListPage((p) => p - 1);
          scrollTimeout.current = setTimeout(() => {
            scrollTimeout.current = null;
          }, 400);
        }
      }
    },
    [step, agentListPage, agentsArray.length],
  );

  const buildInput = (): Record<string, unknown> =>
    inputType === "media"
      ? { type: "media", ids: parsedMediaIds }
      : { type: inputType, folder: folder.trim() };

  const handleOk = (saveNow: boolean = false) => {
    if (!saveNow && step < LAST_STEP) {
      if (canProceed) {
        if (step === 4 && !configName.trim()) {
          const agentObj = agentsArray.find(
            (a) => (a.id || a["@id"]) === selectedAgent,
          );
          const agentLabel =
            agentObj?.name || agentObj?.aiConnection?.model || "Agent";
          let targetLabel = "Standalone";
          if (selectedRuleAction) {
            const cap = classifyCapabilities.find(
              (c) => c.ruleActionId === selectedRuleAction,
            );
            if (cap) targetLabel = cap.label;
          } else if (standaloneCapability === MAIL_DIGEST) {
            targetLabel = "Mail digest";
          } else if (standaloneCapability === IMAGE_EXTRACT) {
            targetLabel = "Image extraction";
          }
          setConfigName(`${agentLabel} - ${targetLabel}`);
        }
        setStep(step + 1);
      }
      return;
    }

    let finalConfigName = configName.trim();
    if (!finalConfigName) {
      const agentObj = agentsArray.find(
        (a) => (a.id || a["@id"]) === selectedAgent,
      );
      const agentLabel =
        agentObj?.name || agentObj?.aiConnection?.model || "Agent";
      let targetLabel = "Standalone";
      if (selectedRuleAction) {
        const cap = classifyCapabilities.find(
          (c) => c.ruleActionId === selectedRuleAction,
        );
        if (cap) targetLabel = cap.label;
      } else if (standaloneCapability === MAIL_DIGEST) {
        targetLabel = "Mail digest";
      } else if (standaloneCapability === IMAGE_EXTRACT) {
        targetLabel = "Image extraction";
      }
      finalConfigName = `${agentLabel} - ${targetLabel}`;
      setConfigName(finalConfigName);
    }

    if (!selectedAgent || !benchmarkType || testDataMissing) return;

    mutation.mutate({
      id: editingConfigId || undefined,
      name: finalConfigName,
      agentId: selectedAgent,
      ruleActionId: selectedRuleAction ?? undefined,
      config: {
        type: benchmarkType,
        input: buildInput(),
        limit: parameters.limit,
        parameters: buildParameters(parameters),
        judge: judging.enabled
          ? {
              enabled: true,
              mode: judging.mode,
              override: judging.override ?? undefined,
              aiProfileId: judging.aiProfileId ?? undefined,
              instructions: judging.instructions?.trim() || undefined,
              criteria: judging.criteria?.trim() || undefined,
            }
          : { enabled: false },
      },
    });
  };

  const handleCancel = () => {
    if (step === 0) closeAndReset();
    else setStep(step - 1);
  };

  return (
    <>
      <ButtonGroup className="flex mb-4">
        <Button
          className="ml-auto"
          variant="primary"
          onClick={() => setIsModalOpen(true)}
        >
          Create New Benchmark Config
        </Button>
      </ButtonGroup>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setSearchParams(
              (prev) => {
                prev.set("tab", key);
                return prev;
              },
              { replace: true },
            );
          }}
          items={[
            {
              key: "configs",
              label: "Configurations",
              children: (
                <Suspense
                  fallback={
                    <CardContent>
                      <Loading />
                    </CardContent>
                  }
                >
                  <BenchmarkConfigsTable
                    agents={agentsArray}
                    ruleActions={ruleActionsArray}
                    aiProfiles={aiProfilesArray}
                    onRunStarted={(runId) => setActiveRunId(runId)}
                    onEdit={(record) => {
                      setEditingConfigId(record.id);
                      setConfigName(record.name);
                      setSelectedAgent(
                        typeof record.agent === "string"
                          ? record.agent
                          : record.agent.id,
                      );
                      setSelectedRuleAction(record.ruleActionId || null);
                      const conf = record.config || {};
                      setStandaloneCapability(
                        conf.type === MAIL_DIGEST
                          ? MAIL_DIGEST
                          : conf.type === IMAGE_EXTRACT
                            ? IMAGE_EXTRACT
                            : null,
                      );
                      setInputType(conf.input?.type || "image_folder");
                      setFolder(conf.input?.folder || "");
                      if (conf.input?.type === "media" && conf.input?.ids) {
                        setMediaIds(conf.input.ids.join(", "));
                      }

                      // The agent and folder list pages will be calculated by the useEffect
                      // once the agentsData and foldersData are available.

                      const p = conf.parameters || {};
                      setParameters({
                        override: p.override ?? null,
                        aiProfileId: p.aiProfileId ?? null,
                        instructions: p.instructions || "",
                        maxTokensPerSession: p.maxTokensPerSession ?? null,
                        limit: conf.limit ?? 1,
                      });

                      const j = conf.judge || { enabled: false };
                      setJudging({
                        enabled: j.enabled,
                        mode: j.mode || "ground_truth",
                        override: j.override ?? null,
                        aiProfileId: j.aiProfileId ?? null,
                        instructions: j.instructions || "",
                        criteria: j.criteria || "",
                      });

                      setStep(0);
                      setIsModalOpen(true);
                    }}
                  />
                </Suspense>
              ),
            },
            {
              key: "runs",
              label: "Recent Runs",
              children: (
                <Suspense
                  fallback={
                    <CardContent>
                      <Loading />
                    </CardContent>
                  }
                >
                  <BenchmarkRunsTable
                    agents={agentsArray}
                    ruleActions={ruleActionsArray}
                    aiProfiles={aiProfilesArray}
                  />
                </Suspense>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={
          editingConfigId
            ? "Edit Benchmark Configuration"
            : "New Benchmark Configuration"
        }
        open={isModalOpen}
        onCancel={closeAndReset}
        footer={[
          <AntButton key="back" onClick={handleCancel}>
            {step === 0 ? "Cancel" : "Back"}
          </AntButton>,
          step > 1 && canProceed && step < LAST_STEP ? (
            <AntButton
              key="save-early"
              loading={mutation.isPending}
              onClick={() => handleOk(true)}
              className="bg-green-50 text-green-600 border-green-200 hover:bg-green-100 font-semibold"
            >
              Save & Close
            </AntButton>
          ) : null,
          <AntButton
            key="submit"
            type="primary"
            loading={mutation.isPending}
            disabled={
              step < LAST_STEP ? !canProceed : !canStart && !configName.trim()
            }
            onClick={() => handleOk(false)}
          >
            {step < LAST_STEP
              ? `Next: ${STEP_TITLES[step + 1]}`
              : "Save Configuration"}
          </AntButton>,
        ].filter(Boolean)}
        width={800}
        destroyOnHidden
      >
        <Steps
          current={step}
          size="small"
          items={STEP_TITLES.map((title, index) => {
            let disabled = false;
            if (index > 0 && !selectedAgent) disabled = true;
            if (index > 1 && !benchmarkType) disabled = true;
            if (index > 2 && testDataMissing) disabled = true;
            return { title, disabled };
          })}
          onChange={setStep}
          className="mb-6 mt-2"
        />

        <div className="py-2 min-h-[20rem]" onWheel={handleWheel}>
          {step === 0 && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Select Agent to Benchmark
              </label>
              <Spin spinning={agentsLoading}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {agentsArray
                    .slice((agentListPage - 1) * 4, agentListPage * 4)
                    .map((agent: any) => (
                      <div key={agent.id || agent["@id"]}>
                        <AgentTile
                          agent={agent}
                          selected={
                            agentUuid(selectedAgent) ===
                            agentUuid(agent.id || agent["@id"])
                          }
                          onSelect={() => selectAgent(agent)}
                          onDoubleClick={() => {
                            selectAgent(agent);
                            setStep(1);
                          }}
                        />
                      </div>
                    ))}
                </div>
                {agentsArray.length > 4 && (
                  <div className="flex justify-end mt-4">
                    <Pagination
                      size="small"
                      current={agentListPage}
                      pageSize={4}
                      total={agentsArray.length}
                      onChange={(page) => setAgentListPage(page)}
                    />
                  </div>
                )}
              </Spin>
            </>
          )}

          {step === 1 && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                What do you want to benchmark?
              </label>
              <Spin spinning={ruleActionsLoading}>
                <div className="grid grid-cols-2 gap-4">
                  {classifyCapabilities.map(
                    (cap: {
                      type: string;
                      label: string;
                      description: string;
                      ruleActionId: string;
                    }) => {
                      const selected =
                        agentUuid(selectedRuleAction) ===
                        agentUuid(cap.ruleActionId);
                      return (
                        <div key={cap.ruleActionId}>
                          <CapabilityTile
                            title={cap.label}
                            description={cap.description}
                            selected={selected}
                            onSelect={() => selectRuleAction(cap.ruleActionId)}
                            onDoubleClick={() => {
                              selectRuleAction(cap.ruleActionId);
                              setStep(2);
                            }}
                          />
                        </div>
                      );
                    },
                  )}
                </div>
              </Spin>

              {showStandaloneFallback && (
                <div className="mt-6">
                  <p className="text-gray-500 text-sm mb-2 m-0">
                    This agent has no classification rule action. You can
                    benchmark it as a standalone capability:
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      ...(selectedAgentObj?.hasVision
                        ? [
                            {
                              key: IMAGE_EXTRACT,
                              onSelect: selectImageExtraction,
                              selected: standaloneCapability === IMAGE_EXTRACT,
                              title: "Image extraction (photos)",
                              description:
                                "Feed images to the agent and check the structured data it extracts.",
                            },
                          ]
                        : []),
                      {
                        key: MAIL_DIGEST,
                        onSelect: selectMailDigest,
                        selected: standaloneCapability === MAIL_DIGEST,
                        title: "Mail digest (emails)",
                        description:
                          "Feed .eml emails to the agent and check the dossier it extracts.",
                      },
                    ].map((cap) => (
                      <div key={cap.key}>
                        <CapabilityTile
                          title={cap.title}
                          description={cap.description}
                          selected={cap.selected}
                          onSelect={cap.onSelect}
                          onDoubleClick={() => {
                            cap.onSelect();
                            setStep(2);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  How should we benchmark it? (capability)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {inputSources.map((src) => {
                    const isSelected = inputType === src.value;
                    const isImageFolder = src.value === "image_folder";
                    const agentObj = agentsArray.find(
                      (a) => (a.id || a["@id"]) === selectedAgent,
                    );
                    const hasVision = agentObj?.hasVision;

                    const disabled = isImageFolder && !hasVision;

                    return (
                      <div key={src.value}>
                        <Tooltip
                          title={
                            disabled
                              ? "The selected agent must have vision capabilities enabled to use an image folder."
                              : ""
                          }
                        >
                          <div
                            role="button"
                            tabIndex={disabled ? -1 : 0}
                            aria-disabled={disabled}
                            aria-pressed={isSelected}
                            onClick={() =>
                              !disabled && selectInputType(src.value)
                            }
                            onKeyDown={(e) => {
                              if (disabled) {
                                return;
                              }
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                selectInputType(src.value);
                              }
                            }}
                            className={`text-center transition-all border p-4 rounded-lg ${
                              disabled
                                ? "opacity-50 cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                                : isSelected
                                  ? "cursor-pointer border-blue-500 bg-blue-50 text-blue-700 font-medium"
                                  : "cursor-pointer border-gray-200 bg-white hover:border-gray-300 text-gray-700"
                            }`}
                          >
                            {src.label}
                            {disabled && (
                              <span className="block text-xs mt-1">
                                (Requires Vision)
                              </span>
                            )}
                          </div>
                        </Tooltip>
                      </div>
                    );
                  })}
                </div>
              </div>

              {FOLDER_SOURCES.includes(inputType) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {inputType === "eml_folder"
                      ? "Select a folder with emails"
                      : "Select a folder with images"}
                  </label>
                  {folderOptions.length >= 0 && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        // @ts-ignore: React types don't officially support webkitdirectory yet
                        webkitdirectory="true"
                        directory="true"
                        multiple
                        onChange={handleFolderSelect}
                      />
                      {(() => {
                        const items = [...folderOptions, "__UPLOAD__"];
                        if (inputType === "eml_folder") {
                          items.push("__GENERATE__");
                        }
                        const paginated =
                          items.length > 6
                            ? items.slice(
                                (folderListPage - 1) * 6,
                                folderListPage * 6,
                              )
                            : items;
                        return (
                          <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              {paginated.map((f) => {
                                if (f === "__UPLOAD__") {
                                  return (
                                    <div key="__UPLOAD__">
                                      <div
                                        onClick={() =>
                                          !uploadMutation.isPending &&
                                          fileInputRef.current?.click()
                                        }
                                        className={`cursor-pointer flex items-center justify-center transition-all border border-dashed p-4 rounded-lg h-full ${uploadMutation.isPending ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-blue-500 hover:bg-blue-50"}`}
                                      >
                                        {uploadMutation.isPending ? (
                                          <div className="flex flex-col items-center">
                                            <Spin />
                                            {uploadProgress !== null && (
                                              <span className="text-sm mt-2 text-blue-600 font-medium">
                                                {uploadProgress}%
                                              </span>
                                            )}
                                          </div>
                                        ) : (
                                          <>
                                            <UploadOutlined className="text-2xl mr-3 text-blue-500" />
                                            <h4 className="text-base font-medium text-blue-600 m-0">
                                              Upload new folder...
                                            </h4>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  );
                                }

                                if (f === "__GENERATE__") {
                                  return (
                                    <div key="__GENERATE__">
                                      <div
                                        onClick={() =>
                                          setIsGenerateModalOpen(true)
                                        }
                                        className="cursor-pointer flex flex-col items-center justify-center transition-all border border-dashed p-4 rounded-lg h-full border-gray-300 bg-gray-50 hover:border-purple-500 hover:bg-purple-50"
                                      >
                                        <RobotOutlined className="text-2xl mb-2 text-purple-500" />
                                        <h4 className="text-base font-medium text-purple-600 m-0">
                                          Generate new emails...
                                        </h4>
                                        <span className="text-xs text-gray-500 mt-1">
                                          AI synthetic damage reports
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }

                                // Type cast required because the array contains strings ("__UPLOAD__") AND objects (BenchmarkFolderDto)
                                const folderDto = f as unknown as {
                                  id: string;
                                  name: string;
                                  count: number;
                                };
                                const isSelected = folder === folderDto.id;
                                return (
                                  <div key={folderDto.id}>
                                    <div
                                      onClick={() => setFolder(folderDto.id)}
                                      onDoubleClick={() => {
                                        setFolder(folderDto.id);
                                        setStep(3);
                                      }}
                                    >
                                      <div
                                        className={`group relative cursor-pointer flex items-center transition-all border p-4 rounded-lg ${isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white hover:border-gray-300"}`}
                                      >
                                        <FolderOutlined
                                          className={`text-2xl mr-3 flex-shrink-0 ${isSelected ? "text-blue-500" : "text-gray-400"}`}
                                        />
                                        <div className="flex flex-col min-w-0 pr-12">
                                          <Tooltip
                                            title={folderDto.name
                                              .replace(/[-_]/g, " ")
                                              .replace(/\b\w/g, (l) =>
                                                l.toUpperCase(),
                                              )}
                                            placement="topLeft"
                                          >
                                            <h4 className="text-base font-medium text-gray-900 m-0 truncate">
                                              {folderDto.name
                                                .replace(/[-_]/g, " ")
                                                .replace(/\b\w/g, (l) =>
                                                  l.toUpperCase(),
                                                )}
                                            </h4>
                                          </Tooltip>
                                          <span className="text-xs text-gray-500">
                                            {folderDto.count} files
                                          </span>
                                        </div>

                                        <div
                                          className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Tooltip title="Preview contents">
                                            <div
                                              role="button"
                                              tabIndex={0}
                                              className="p-1.5 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded cursor-pointer flex items-center justify-center transition-colors"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewFolder(folderDto.id);
                                              }}
                                              onKeyDown={(e) => {
                                                if (
                                                  e.key === "Enter" ||
                                                  e.key === " "
                                                ) {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  setPreviewFolder(
                                                    folderDto.id,
                                                  );
                                                }
                                              }}
                                            >
                                              <EyeOutlined />
                                            </div>
                                          </Tooltip>
                                          <Tooltip title="Rename folder">
                                            <div
                                              className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded cursor-pointer flex items-center justify-center transition-colors"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const newName = window.prompt(
                                                  "Enter new folder name:",
                                                  folderDto.id,
                                                );
                                                if (
                                                  newName &&
                                                  newName.trim() !==
                                                    folderDto.id
                                                ) {
                                                  renameFolderMutation.mutate({
                                                    type: inputType,
                                                    folderName: folderDto.id,
                                                    newName: newName.trim(),
                                                  });
                                                }
                                              }}
                                            >
                                              <EditOutlined />
                                            </div>
                                          </Tooltip>

                                          <Tooltip title="Delete folder">
                                            <Popconfirm
                                              title="Delete the folder?"
                                              description="Are you sure you want to permanently delete this folder from the server?"
                                              onConfirm={(e) => {
                                                e?.stopPropagation();
                                                deleteFolderMutation.mutate({
                                                  type: inputType,
                                                  folderName: folderDto.id,
                                                });
                                              }}
                                              onCancel={(e) =>
                                                e?.stopPropagation()
                                              }
                                            >
                                              <div
                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded cursor-pointer flex items-center justify-center transition-colors"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <DeleteOutlined />
                                              </div>
                                            </Popconfirm>
                                          </Tooltip>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {items.length > 6 && (
                              <div className="flex justify-end mt-4">
                                <Pagination
                                  size="small"
                                  current={folderListPage}
                                  pageSize={6}
                                  total={items.length}
                                  onChange={setFolderListPage}
                                />
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}

                  <p className="text-gray-500 text-xs mt-1 mb-6">
                    {inputType === "eml_folder" ? (
                      <>
                        Subfolder of <code>benchmark-emails/</code> holding{" "}
                        <code>.eml</code> files — one result per email. Add an
                        optional <code>expected.json</code> (expected dossier
                        fields) for ground-truth scoring.
                      </>
                    ) : (
                      <>
                        Subfolder of <code>benchmark-images/</code> the worker
                        classifies — one result per image. Add an optional{" "}
                        <code>expected.json</code> for ground-truth scoring.
                      </>
                    )}
                  </p>

                  {folder &&
                    (() => {
                      const selectedFolderObj = (
                        folderOptions as unknown as {
                          id: string;
                          count: number;
                        }[]
                      ).find((f) => f.id === folder);
                      const maxCases = selectedFolderObj?.count || 100;
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
                          <div className="flex justify-between items-center mb-4">
                            <label className="block text-sm font-semibold text-gray-800 m-0">
                              Test Case Limit
                            </label>
                            <Switch
                              checked={
                                parameters.limit !== null &&
                                parameters.limit > 0
                              }
                              onChange={(checked) =>
                                setParameters((prev) => ({
                                  ...prev,
                                  limit: checked
                                    ? Math.min(10, maxCases)
                                    : null,
                                }))
                              }
                              checkedChildren="Enabled"
                              unCheckedChildren="Disabled"
                            />
                          </div>

                          {parameters.limit !== null && parameters.limit > 0 ? (
                            <>
                              <div className="flex items-start gap-6 mb-4">
                                <Slider
                                  className="flex-grow mt-2"
                                  min={1}
                                  max={maxCases}
                                  value={parameters.limit || maxCases}
                                  onChange={(v) =>
                                    setParameters((prev) => ({
                                      ...prev,
                                      limit: v,
                                    }))
                                  }
                                  disabled={!selectedFolderObj}
                                  tooltip={{
                                    formatter: (val) =>
                                      val === maxCases ? "All" : val,
                                  }}
                                  marks={{
                                    1: (
                                      <span className="text-gray-400 text-xs">
                                        1
                                      </span>
                                    ),
                                    [maxCases]: (
                                      <span className="text-gray-400 text-xs font-semibold">
                                        {selectedFolderObj
                                          ? `${maxCases} (All)`
                                          : maxCases}
                                      </span>
                                    ),
                                  }}
                                />
                                <InputNumber
                                  className="w-40 text-lg shadow-sm text-center font-mono"
                                  size="large"
                                  min={1}
                                  max={maxCases}
                                  precision={0}
                                  value={parameters.limit}
                                  onChange={(v) =>
                                    setParameters((prev) => ({
                                      ...prev,
                                      limit: v ? Number(v) : null,
                                    }))
                                  }
                                  placeholder={
                                    selectedFolderObj
                                      ? `No limit (${maxCases})`
                                      : "No limit"
                                  }
                                />
                              </div>
                              <p className="text-gray-500 text-xs mt-3 mb-0 leading-relaxed bg-white p-3 rounded border border-gray-100">
                                {selectedFolderObj ? (
                                  <span>
                                    This folder contains{" "}
                                    <strong>{maxCases} items</strong>. The
                                    benchmark will only run a random sample of{" "}
                                    {parameters.limit} cases.
                                  </span>
                                ) : (
                                  <span>
                                    Select a folder to see how many test cases
                                    it contains.
                                  </span>
                                )}
                                <br />
                                <span className="text-gray-400 block mt-1">
                                  Limiting test cases is highly recommended when
                                  testing new prompts to manage LLM token costs.
                                </span>
                              </p>
                            </>
                          ) : (
                            <p className="text-gray-500 text-xs mt-0 mb-0 leading-relaxed bg-white p-3 rounded border border-gray-100">
                              {selectedFolderObj ? (
                                <span>
                                  This folder contains{" "}
                                  <strong>{maxCases} items</strong>. The
                                  benchmark will run{" "}
                                  <strong>all {maxCases} cases</strong>.
                                </span>
                              ) : (
                                <span>
                                  Select a folder to see how many test cases it
                                  contains.
                                </span>
                              )}
                              <br />
                              <span className="text-gray-400 block mt-1">
                                Warning: Running all test cases can consume a
                                lot of LLM tokens. Enable the limit for testing.
                              </span>
                            </p>
                          )}
                        </div>
                      );
                    })()}
                </div>
              )}

              {inputType === "media" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Media usage ids
                  </label>
                  <Select
                    mode="tags"
                    className="w-full"
                    placeholder="Type an ID and press Enter"
                    value={parsedMediaIds}
                    onChange={(val) => setMediaIds(val.join(" "))}
                    tokenSeparators={[",", " ", "\n"]}
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Existing media records to classify, by usage id — one result
                    per record. Unknown ids are skipped.
                    {parsedMediaIds.length > 0 &&
                      ` (${parsedMediaIds.length} id${parsedMediaIds.length === 1 ? "" : "s"})`}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <BenchmarkParametersStep
              values={parameters}
              onChange={(patch) =>
                setParameters((prev) => ({ ...prev, ...patch }))
              }
              aiConnection={selectedAgentConnection}
              modelCatalog={modelCatalog}
              agentProfile={selectedAgentProfile}
            />
          )}

          {step === 4 && (
            <BenchmarkJudgingStep
              values={judging}
              onChange={(patch) =>
                setJudging((prev) => ({ ...prev, ...patch }))
              }
              allowImageMode={
                benchmarkType === CLASSIFY_BY_AGENT ||
                benchmarkType === IMAGE_EXTRACT
              }
            />
          )}

          {step === 5 && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Name your configuration
              </label>
              <Input
                value={configName}
                onChange={(e) => setConfigName(e.target.value)}
                placeholder="e.g. Daily Car Angle Run"
                size="large"
                autoFocus
              />
            </>
          )}
        </div>
      </Modal>

      <GenerateSyntheticEmailsModal
        open={isGenerateModalOpen}
        onCancel={() => setIsGenerateModalOpen(false)}
        onSuccess={(folderName) => {
          setIsGenerateModalOpen(false);
          setFolder(folderName);
        }}
      />

      <FolderContentsViewerModal
        type={inputType}
        folderName={previewFolder || ""}
        visible={!!previewFolder}
        onClose={() => setPreviewFolder(null)}
      />

      {activeRunId && (
        <RunConsoleModal
          runId={activeRunId}
          onClose={() => setActiveRunId(null)}
        />
      )}
    </>
  );
}
